import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import CountryCodeSelect from '../components/CountryCodeSelect';
import { validLocalPhone, composePhone, localPlaceholder } from '../utils/phone';

/* 轮询 scene 登录状态(PC扫码/手机去微信登录 共用),确认即写 token 跳转 */
function pollScene(scene, isStopped, onExpired) {
  const started = Date.now();
  function poll() {
    if (isStopped()) return;
    if (Date.now() - started > 175000) { onExpired(); return; }
    fetch('/api/auth/wx-scan/status?scene=' + scene)
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (d.status === 'confirmed') {
          localStorage.setItem('token', d.token);
          localStorage.setItem('user', JSON.stringify(d.user));
          window.location.replace('/auction-hub');
        } else if (d.status === 'expired') { onExpired(); }
        else { setTimeout(poll, 2000); }
      })
      .catch(function () { setTimeout(poll, 2000); });
  }
  setTimeout(poll, 2000);
}

async function startWxScanLogin() {
  try {
    const res = await fetch('/api/auth/wx-scan/create', { method: 'POST' });
    const data = await res.json();
    const old = document.getElementById('wx-scan-overlay');
    if (old) old.remove();
    const ov = document.createElement('div');
    ov.id = 'wx-scan-overlay';
    ov.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;z-index:9999';
    ov.innerHTML = '<div style="background:#fff;padding:28px 32px;border-radius:8px;text-align:center;max-width:320px">' +
      '<div style="font-size:15px;font-weight:600;margin-bottom:14px;color:#333">微信扫码登录</div>' +
      '<img src="' + data.qr + '" style="width:220px;height:220px" alt="qr"/>' +
      '<div id="wx-scan-tip" style="font-size:13px;color:#666;margin-top:10px">请用手机微信扫一扫<br/>并在手机上完成微信登录</div>' +
      '<button id="wx-scan-close" style="margin-top:14px;padding:6px 18px;border:1px solid #ccc;background:#fff;border-radius:4px;cursor:pointer">取消</button></div>';
    document.body.appendChild(ov);
    let stop = false;
    ov.querySelector('#wx-scan-close').onclick = function () { stop = true; ov.remove(); };
    pollScene(data.scene, function () { return stop; }, function () {
      const tip = document.getElementById('wx-scan-tip');
      if (tip) tip.textContent = '二维码已过期,请关闭后重试';
    });
  } catch (e) {
    alert('生成二维码失败,请稍后重试');
  }
}

/* 手机浏览器(非微信内)微信登录:免扫码。
 * 首选:小程序 URL Link —— 点按钮直接拉起微信打开小程序确认页,一键授权(真·一键)。
 * 回退:URL Link 不可用(未发版/白名单/配额)时,复制专属链接引导去微信粘贴打开。 */
async function startWxMobileLogin() {
  try {
    const res = await fetch('/api/auth/wx-scan/create', { method: 'POST' });
    const data = await res.json();

    // ── 首选:小程序 URL Link 直拉微信 ──
    try {
      const mlRes = await fetch('/api/auth/wx-scan/mplink', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scene: data.scene }),
      });
      if (mlRes.ok) {
        const ml = await mlRes.json();
        if (ml.url_link) {
          const old = document.getElementById('wx-scan-overlay');
          if (old) old.remove();
          const ov = document.createElement('div');
          ov.id = 'wx-scan-overlay';
          ov.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:flex-end;justify-content:center;z-index:9999';
          ov.innerHTML = '<div style="background:#fff;padding:26px 22px 32px;border-radius:14px 14px 0 0;width:100%;max-width:480px;box-sizing:border-box;text-align:center">' +
            '<div style="font-size:16px;font-weight:600;margin-bottom:8px;color:#333">💬 正在拉起微信…</div>' +
            '<div id="wx-scan-tip" style="font-size:13px;color:#666;line-height:1.9;margin-bottom:14px">在微信中点一下「授权登录」即可<br/>完成后回到本页面自动登录</div>' +
            '<button id="wx-relaunch" style="width:100%;padding:12px 0;border:none;border-radius:8px;background:#07C160;color:#fff;font-size:15px;font-weight:600;cursor:pointer">未跳转?点此打开微信</button>' +
            '<button id="wx-scan-close" style="width:100%;margin-top:10px;padding:10px 0;border:1px solid #ddd;background:#fff;border-radius:8px;cursor:pointer;font-size:13px;color:#555">取消</button></div>';
          document.body.appendChild(ov);
          let stop = false;
          ov.querySelector('#wx-scan-close').onclick = function () { stop = true; ov.remove(); };
          ov.querySelector('#wx-relaunch').onclick = function () { window.location.href = ml.url_link; };
          pollScene(data.scene, function () { return stop; }, function () {
            const tip = document.getElementById('wx-scan-tip');
            if (tip) { tip.textContent = '链接已过期,请关闭后重试'; tip.style.color = '#c0392b'; }
          });
          window.location.href = ml.url_link; // 直接拉起微信小程序
          return;
        }
      }
    } catch { /* URL Link 不可用 → 走复制链接回退 */ }

    const link = window.location.origin + '/login?scan=' + data.scene;
    let copied = false;
    try { await navigator.clipboard.writeText(link); copied = true; } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = link; ta.style.cssText = 'position:fixed;opacity:0';
        document.body.appendChild(ta); ta.select();
        copied = document.execCommand('copy'); ta.remove();
      } catch { /* 复制失败也展示链接让用户长按复制 */ }
    }
    const old = document.getElementById('wx-scan-overlay');
    if (old) old.remove();
    const ov = document.createElement('div');
    ov.id = 'wx-scan-overlay';
    ov.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:flex-end;justify-content:center;z-index:9999';
    ov.innerHTML = '<div style="background:#fff;padding:24px 22px 30px;border-radius:14px 14px 0 0;width:100%;max-width:480px;box-sizing:border-box">' +
      '<div style="font-size:16px;font-weight:600;margin-bottom:6px;color:#333">💬 去微信完成一键登录</div>' +
      '<div id="wx-scan-tip" style="font-size:13px;color:#07C160;margin-bottom:12px">' + (copied ? '✓ 专属登录链接已复制' : '请长按下方链接复制') + '</div>' +
      '<div style="font-size:13px;color:#555;line-height:2;margin-bottom:12px">' +
      '1. 点「打开微信」<br/>2. 粘贴到任意聊天(如<b>文件传输助手</b>)并点击链接<br/>3. 微信里点一下授权 → 回到本页自动登录</div>' +
      '<div style="font-size:11px;color:#999;word-break:break-all;background:#f7f7f7;border-radius:6px;padding:8px 10px;margin-bottom:14px;user-select:all">' + link + '</div>' +
      '<button id="wx-open-app" style="width:100%;padding:12px 0;border:none;border-radius:8px;background:#07C160;color:#fff;font-size:15px;font-weight:600;cursor:pointer">打开微信</button>' +
      '<div style="display:flex;gap:10px;margin-top:10px">' +
      '<button id="wx-copy-again" style="flex:1;padding:10px 0;border:1px solid #ddd;background:#fff;border-radius:8px;cursor:pointer;font-size:13px;color:#555">再次复制链接</button>' +
      '<button id="wx-scan-close" style="flex:1;padding:10px 0;border:1px solid #ddd;background:#fff;border-radius:8px;cursor:pointer;font-size:13px;color:#555">取消</button></div></div>';
    document.body.appendChild(ov);
    let stop = false;
    ov.querySelector('#wx-scan-close').onclick = function () { stop = true; ov.remove(); };
    ov.querySelector('#wx-open-app').onclick = function () { window.location.href = 'weixin://'; };
    ov.querySelector('#wx-copy-again').onclick = function () {
      const ta = document.createElement('textarea');
      ta.value = link; ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { }
      ta.remove();
      const tip = document.getElementById('wx-scan-tip');
      if (tip) tip.textContent = '✓ 已复制,去微信粘贴打开';
    };
    pollScene(data.scene, function () { return stop; }, function () {
      const tip = document.getElementById('wx-scan-tip');
      if (tip) { tip.textContent = '链接已过期,请关闭后重试'; tip.style.color = '#c0392b'; }
    });
  } catch (e) {
    alert('生成登录链接失败,请稍后重试');
  }
}


const GOLD = '#B8892A';
const BD = '#E8E0D0';
const MUTED = '#6B5E4E';

export default function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login } = useAuth();
  const loginTab = 'customer';
  const [method, setMethod] = useState('phone');
  const [countryCode, setCountryCode] = useState('+86');
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [msg, setMsg] = useState(null);
  const timerRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 微信内打开「专属登录链接」(/login?scan=xx,粘贴/扫码进来)时自动拉起一键授权,
  // 用户只需点一次「允许」。token/wx_error 在场时不触发(前者=已回跳,后者=刚失败防死循环)。
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isWx = /MicroMessenger/i.test(navigator.userAgent);
    if (isWx && params.get('scan') && !params.get('token') && !params.get('wx_error')) {
      (async () => {
        try {
          const { data: signData } = await api.post('/auth/wx-js-sign', { url: window.location.href.split('#')[0] });
          const redirectUri = encodeURIComponent('https://ycvintage.com/api/auth/wx-web-callback?state=' + encodeURIComponent(window.location.pathname + window.location.search));
          window.location.href = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${signData.appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_userinfo&state=login#wechat_redirect`;
        } catch { /* 拉起失败则留在登录页,用户可手动点微信一键登录 */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 发码冷却：把结束时刻(ms)存 localStorage，刷新/跨页不重置，防止刷新绕过冷却狂点
  const CD_KEY = 'sms_cd_until';
  const runCountdown = (until) => {
    if (timerRef.current) clearInterval(timerRef.current);
    const upd = () => {
      const r = Math.max(0, Math.ceil((until - Date.now()) / 1000));
      setSmsCountdown(r);
      if (r <= 0) { clearInterval(timerRef.current); try { localStorage.removeItem(CD_KEY); } catch {} }
    };
    upd();
    timerRef.current = setInterval(upd, 1000);
  };
  const startCountdown = (secs = 60) => {
    const until = Date.now() + secs * 1000;
    try { localStorage.setItem(CD_KEY, String(until)); } catch {}
    runCountdown(until);
  };
  // 进入页面时恢复尚未结束的冷却倒计时
  useEffect(() => {
    try {
      const until = parseInt(localStorage.getItem(CD_KEY) || '0', 10);
      if (until > Date.now()) runCountdown(until);
    } catch {}
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendSMS = async () => {
    if (!phone) { setMsg({ text: t('auth.phonePlaceholder'), type: 'err' }); return; }
    if (!validLocalPhone(countryCode, phone)) { setMsg({ text: '手机号格式不正确', type: 'err' }); return; }
    if (smsCountdown > 0) return; // 冷却中，避免重复发
    try {
      // 成功才启动倒计时；失败时显示后端真实错误（如"发送过于频繁，请稍后再试"）
      // phone 统一为存储格式（中国裸号 / 国际 E.164），与登录时的查询格式一致
      await api.post('/auth/send-sms', { phone: composePhone(countryCode, phone), country_code: countryCode });
      setMsg({ text: `${t('forgotPassword.sendSuccess')} ${phone}`, type: 'ok' });
      startCountdown(60);
    } catch (err) {
      setMsg({ text: err?.response?.data?.error || err?.response?.data?.message || t('common.failed'), type: 'err' });
    }
  };

  const doLogin = async () => {
    setMsg(null);
    if (method === 'phone') {
      if (!phone || !smsCode) { setMsg({ text: t('auth.phonePlaceholder'), type: 'err' }); return; }
    } else {
      if (!email || !password) { setMsg({ text: t('auth.phonePlaceholder'), type: 'err' }); return; }
    }
    setLoading(true);
    try {
      await login(method === 'phone' ? composePhone(countryCode, phone) : email, method === 'phone' ? smsCode : password);
      setMsg({ text: t('auth.loginSuccess'), type: 'ok' });
      setTimeout(() => navigate('/auction-hub', { replace: true }), 800);
    } catch (err) {
      setMsg({ text: err?.response?.data?.message || t('common.failed'), type: 'err' });
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '13px 15px',
    border: '1px solid ' + BD, borderRadius: 3,
    fontFamily: 'var(--mono)', fontSize: 14, color: '#110E0A',
    background: '#FAFAF8', outline: 'none',
    transition: 'all 0.18s',
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#FAFAF8', overflow: 'hidden' }}>
      {/* Left panel - dark theme (desktop only) */}
      <div style={{
        width: '52%', position: 'relative', display: isMobile ? 'none' : 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: '#110E0A',
      }}>
        {/* Grid pattern overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `linear-gradient(rgba(184,137,42,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(184,137,42,0.05) 1px, transparent 1px)`,
          backgroundSize: '48px 48px', pointerEvents: 'none',
        }} />
        {/* Warm gradient glow */}
        <div style={{
          position: 'absolute', top: '30%', left: '40%', width: 400, height: 400,
          background: 'radial-gradient(circle, rgba(184,137,42,0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 360 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginBottom: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 8, background: '#8C1F1F', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F5E6D0', fontSize: 14, fontWeight: 700, lineHeight: 1.1, textAlign: 'center', letterSpacing: 1 }}>云川<br/>中古</div>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 36, fontWeight: 700, color: GOLD, letterSpacing: 8, lineHeight: 1 }}>云川中古</div>
          </div>
          <div style={{ width: 144, height: 1, background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`, margin: '0 auto 14px' }} />
          <div style={{ fontFamily: 'var(--serif)', fontSize: 11, letterSpacing: 4, color: 'rgba(168,153,138,0.7)' }}>
            YUNCHUAN VINTAGE
          </div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 13, color: 'rgba(168,153,138,0.5)', letterSpacing: 3, marginTop: 10 }}>
            日本専門競拍 · 代理入札サービス
          </div>

          {/* Feature list */}
          <div style={{ marginTop: 48, textAlign: 'left' }}>
            {[
              { icon: '🤖', title: '自动化竞拍引擎', desc: '毫秒级响应，智能出价策略' },
              { icon: '🏯', title: '16家拍卖行直连', desc: '覆盖日本主流拍卖渠道' },
              { icon: '🔒', title: '保证金全额托管', desc: '未中标全额退回，资金安全' },
              { icon: '🔬', title: '专业检品报告', desc: '5维评分，高清实拍照片' },
            ].map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
                <div style={{ fontSize: 20, flexShrink: 0, marginTop: 2 }}>{f.icon}</div>
                <div>
                  <div style={{ fontSize: 13, color: 'rgba(245,240,232,0.85)', fontWeight: 500, marginBottom: 3 }}>{f.title}</div>
                  <div style={{ fontSize: 11, color: 'rgba(168,153,138,0.5)' }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Auction house tags strip */}
        <div style={{ position: 'absolute', bottom: 48, left: 0, right: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', padding: '0 32px' }}>
            {['おたからや', '大吉', 'Eco Auc', 'RS', 'RK', 'APRE', 'JWA', 'REVA', 'Timeless', 'NJ', '大黒屋'].map(h => (
              <span key={h} style={{
                fontFamily: 'var(--mono)', fontSize: 9, color: 'rgba(168,153,138,0.4)',
                padding: '3px 10px', border: '1px solid rgba(184,137,42,0.12)', borderRadius: 3,
                letterSpacing: 1,
              }}>{h}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div style={{ width: isMobile ? '100%' : '50%', display: 'flex', flexDirection: 'column', justifyContent: isMobile ? 'flex-start' : 'center', padding: isMobile ? '0 0 40px' : '0 64px', background: '#fff', overflowY: 'auto' }}>
        <div style={{ maxWidth: 380, width: '100%', margin: '0 auto', padding: isMobile ? '0 20px' : '0' }}>

          {/* Mobile branding header */}
          {isMobile && (
            <div style={{ textAlign: 'center', padding: '36px 0 28px', borderBottom: '1px solid #F0EDE8', marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 4 }}>
                <div style={{ width: 32, height: 32, borderRadius: 4, background: '#8C1F1F', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F5E6D0', fontSize: 10, fontWeight: 700, lineHeight: 1.1, textAlign: 'center', letterSpacing: 1 }}>云川<br/>中古</div>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 20, color: GOLD, letterSpacing: 4 }}>云川中古</div>
              </div>
            </div>
          )}

          <div style={{ marginBottom: 28 }}>
            <div style={{ fontFamily: 'var(--serif)', fontSize: isMobile ? 22 : 26, fontWeight: 600, color: '#110E0A', letterSpacing: 1 }}>{t('auth.login')}</div>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: '#A8998A', letterSpacing: 3, marginTop: 6 }}>MEMBER LOGIN · ログイン</div>
          </div>

          {msg && (
            <div style={{
              padding: '11px 14px', borderRadius: 3, fontSize: 13, marginBottom: 18,
              background: msg.type === 'err' ? 'rgba(155,35,53,0.08)' : 'rgba(42,102,69,0.08)',
              border: `1px solid ${msg.type === 'err' ? 'rgba(155,35,53,0.25)' : 'rgba(42,102,69,0.25)'}`,
              color: msg.type === 'err' ? '#9B2335' : '#2A6645',
            }}>
              {msg.type === 'err' ? '⚠ ' : '✓ '}{msg.text}
            </div>
          )}

          {/* Method tabs */}
          <div style={{ display: 'flex', border: '1px solid ' + BD, borderRadius: 3, overflow: 'hidden', marginBottom: 24 }}>
            {[{ key: 'phone', label: t('auth.phone') + t('auth.login') }, { key: 'password', label: t('auth.password') + t('auth.login') }].map((tab) => (
              <button key={tab.key} onClick={() => { setMethod(tab.key); setMsg(null); }} style={{
                flex: 1, padding: 10, textAlign: 'center',
                fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: 2,
                cursor: 'pointer', background: method === tab.key ? 'rgba(184,137,42,0.1)' : 'transparent',
                color: method === tab.key ? GOLD : '#A8998A', border: 'none',
                borderBottom: method === tab.key ? `2px solid ${GOLD}` : '2px solid transparent',
                transition: 'all 0.2s',
              }}>
                {tab.label}
              </button>
            ))}
          </div>

          {method === 'phone' && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: '#A8998A', textTransform: 'uppercase', marginBottom: 7 }}>{t('auth.phone')}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <CountryCodeSelect value={countryCode} onChange={setCountryCode}
                    style={{ border: '1px solid ' + BD, borderRadius: 3, background: '#FAFAF8', padding: '0 8px' }} />
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={localPlaceholder(countryCode)} style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                    onFocus={(e) => { e.target.style.borderColor = GOLD; e.target.style.boxShadow = '0 0 0 3px rgba(184,137,42,0.07)'; }}
                    onBlur={(e) => { e.target.style.borderColor = BD; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: '#A8998A', textTransform: 'uppercase', marginBottom: 7 }}>{t('auth.verifyCode')}</label>
                <div style={{ display: 'flex', gap: 9 }}>
                  <input type="text" value={smsCode} onChange={(e) => setSmsCode(e.target.value)} placeholder={t('auth.codePlaceholder')} maxLength={6}
                    style={{ ...inputStyle, flex: 1 }}
                    onFocus={(e) => { e.target.style.borderColor = GOLD; }}
                    onBlur={(e) => { e.target.style.borderColor = BD; }}
                  />
                  <button onClick={sendSMS} disabled={smsCountdown > 0} style={{
                    flexShrink: 0, padding: '0 16px', background: 'transparent',
                    border: '1px solid ' + BD, borderRadius: 3,
                    color: smsCountdown > 0 ? '#A8998A' : GOLD,
                    fontFamily: 'var(--mono)', fontSize: 11,
                    cursor: smsCountdown > 0 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
                  }}>
                    {smsCountdown > 0 ? `${smsCountdown}s` : t('auth.sendCode')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {method === 'password' && (
            <div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: '#A8998A', textTransform: 'uppercase', marginBottom: 7 }}>{t('auth.phone')} / {t('auth.email')}</label>
                <input type="text" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('auth.phonePlaceholder')}
                  style={inputStyle}
                  onFocus={(e) => { e.target.style.borderColor = GOLD; }}
                  onBlur={(e) => { e.target.style.borderColor = BD; }}
                />
              </div>
              <div style={{ marginBottom: 20, position: 'relative' }}>
                <label style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: '#A8998A', textTransform: 'uppercase', marginBottom: 7 }}>{t('auth.password')}</label>
                <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  style={{ ...inputStyle, paddingRight: 40 }}
                  onFocus={(e) => { e.target.style.borderColor = GOLD; }}
                  onBlur={(e) => { e.target.style.borderColor = BD; }}
                />
                <span onClick={() => setShowPw(!showPw)} style={{ position: 'absolute', right: 13, bottom: 13, cursor: 'pointer', fontSize: 13, color: '#A8998A' }}>
                  {showPw ? '🙈' : '👁'}
                </span>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
            <div onClick={() => setRemember(!remember)} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
              <div style={{
                width: 14, height: 14, border: '1px solid ' + (remember ? GOLD : BD),
                borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: remember ? GOLD : 'transparent', transition: 'all 0.15s',
              }}>
                {remember && <span style={{ color: '#fff', fontSize: 9, fontWeight: 700 }}>✓</span>}
              </div>
              <span style={{ fontSize: 12, color: '#A8998A' }}>{t('auth.login')}</span>
            </div>
            <button onClick={() => navigate('/forgot-password')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: GOLD, opacity: 0.7 }}>{t('auth.forgotPassword')}</button>
          </div>

          <button onClick={doLogin} disabled={loading} style={{
            width: '100%', padding: 15, background: loading ? '#ccc' : GOLD,
            border: 'none', borderRadius: 3, cursor: loading ? 'not-allowed' : 'pointer',
            color: '#fff', fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 600,
            letterSpacing: 6, transition: 'all 0.2s',
          }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#A07A22'; }}
            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = GOLD; }}
          >
            {loading ? t('common.loading') : t('auth.login')}
          </button>

          {/* 微信登录 — 环境分流(2026-07-04 定版):
              微信内 → 公众号网页授权一键登录(无二维码)
              电脑浏览器 → 保留扫码登录(用户拍板保留)
              手机浏览器(非微信) → 「去微信一键登录」:复制专属链接+拉起微信,
                粘贴打开后自动弹授权(微信规则禁止外部浏览器直接拉起授权页,
                此为最接近一键的合规路径,全程无二维码) */}
          <button onClick={async () => {
            // 公众号网页授权只在微信内有效；微信外跳 OAuth 会撞「请在微信客户端打开链接」死页面
            const isWechat = /MicroMessenger/i.test(navigator.userAgent);
            if (!isWechat) {
              const isMobileUA = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
              if (isMobileUA) startWxMobileLogin(); else startWxScanLogin();
              return;
            }
            try {
              // 先获取签名
              const { data: signData } = await api.post('/auth/wx-js-sign', {
                url: window.location.href.split('#')[0]
              });

              if (isWechat && window.wx) {
                window.wx.config({
                  debug: false,
                  appId: signData.appId,
                  timestamp: signData.timestamp,
                  nonceStr: signData.nonceStr,
                  signature: signData.signature,
                  jsApiList: []
                });
                window.wx.ready(() => {
                  // JS-SDK 就绪后，用隐藏 iframe 方式触发 OAuth
                  const redirectUri = encodeURIComponent('https://ycvintage.com/api/auth/wx-web-callback?state=' + encodeURIComponent(window.location.pathname + window.location.search));
                  window.location.href = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${signData.appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_userinfo&state=login#wechat_redirect`;
                });
                window.wx.error((err) => {
                  console.error('wx.config error:', err);
                  // 降级：直接 OAuth 跳转
                  const redirectUri = encodeURIComponent('https://ycvintage.com/api/auth/wx-web-callback?state=' + encodeURIComponent(window.location.pathname + window.location.search));
                  window.location.href = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${signData.appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_userinfo&state=login#wechat_redirect`;
                });
              } else {
                // 微信内但 jweixin 脚本未加载：直接 OAuth 跳转
                const redirectUri = encodeURIComponent('https://ycvintage.com/api/auth/wx-web-callback?state=' + encodeURIComponent(window.location.pathname + window.location.search));
                window.location.href = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${signData.appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_userinfo&state=login#wechat_redirect`;
              }
            } catch { setMsg({ text: '获取授权链接失败', type: 'err' }); }
          }} style={{
            width: '100%', padding: 14, marginTop: 16, marginBottom: 10,
            background: '#07C160',
            border: 'none', borderRadius: 3,
            cursor: 'pointer',
            color: '#fff', fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 600,
            letterSpacing: 2, transition: 'all 0.2s',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
          }}
            onMouseEnter={(e) => { if (/MicroMessenger/i.test(navigator.userAgent)) e.currentTarget.style.background = '#06AD56'; }}
            onMouseLeave={(e) => { if (/MicroMessenger/i.test(navigator.userAgent)) e.currentTarget.style.background = '#07C160'; }}
          >
            <span><span style={{ marginRight: 6 }}>💬</span> 微信一键登录</span>
            {!/MicroMessenger/i.test(navigator.userAgent) && (
              <span style={{ fontSize: 11, fontWeight: 400, letterSpacing: 0, opacity: 0.95 }}>
                {/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? '跳转微信授权' : '扫码登录'}
              </span>
            )}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '18px 0' }}>
            <div style={{ flex: 1, height: 1, background: BD }} />
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: '#A8998A', letterSpacing: 2 }}>— 或 —</span>
            <div style={{ flex: 1, height: 1, background: BD }} />
          </div>

          <button onClick={() => navigate('/register')} style={{
            width: '100%', padding: 13, background: 'transparent', border: '1px solid ' + BD,
            borderRadius: 3, cursor: 'pointer', color: MUTED,
            fontFamily: 'var(--sans)', fontSize: 13, letterSpacing: 1, transition: 'all 0.2s',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = MUTED; }}
          >
            {t('auth.register')}
          </button>

          <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid rgba(184,137,42,0.07)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#A8998A', fontFamily: 'var(--mono)' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#2A6645', display: 'inline-block' }} />
              SSL 加密连接 · 安全登录
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {['中文', '日本語', 'EN'].map((lang, i) => (
                <span key={lang} style={{ fontSize: 11, color: i === 0 ? GOLD : '#A8998A', cursor: 'pointer', fontFamily: 'var(--mono)' }}>{lang}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
